/**
 * `VesselOverlayPanel` — the desktop side panel pinned over the
 * `VesselLiveTrack` map. Renders current speed / heading / ETA /
 * next port / last-fix relative time, plus a connection status pill
 * fed by `useShipPositionStream`.
 *
 * Layout: logical-property positioning (`top-4 end-4`) so the panel
 * appears on the right in LTR and the left in RTL automatically.
 *
 * Animation policy (`.claude/rules/uncodixify-ui.md`):
 *   - Spinner uses `animate-spin` on a `Loader2`-style SVG (allowed).
 *   - Status pill background switches via solid colours; no pulse, no
 *     bounce, no transform-on-hover.
 *
 * Touch targets (`.claude/rules/switch-component-styling.md`):
 *   - The "Retry now" button gets its 44px touch area from the parent
 *     row's `min-h-[44px] p-2` — NOT applied directly to the button.
 */

import type { SSEStatus } from "#/hooks/useSSE.js";
import type { ShipTrailPoint } from "@hantavirus/shared";
import type { TranslateFn } from "#/lib/i18n.js";

export interface VesselOverlayPanelProps {
  /** Vessel display name from the registry. */
  name: string;
  /** Vessel MMSI for the secondary label. */
  mmsi: string;
  /** Last position in the trail (the visible vessel state). */
  latest: ShipTrailPoint | null;
  /** AIS destination, when known. */
  nextPort: string | null;
  /** AIS ETA (ISO 8601), when known. */
  eta: string | null;
  /** SSE connection lifecycle. */
  status: SSEStatus;
  /** Manual reconnect handler. */
  onRetry: () => void;
  /** Translation function (vesselTrack namespace). */
  t: TranslateFn;
  /** Active locale — drives Intl.RelativeTimeFormat / DateTimeFormat. */
  locale: string;
  /** Extra Tailwind classes applied to the root container. */
  className?: string;
}

const SPINNER_PATH = "M21 12a9 9 0 1 1-6.219-8.56";

/** Spinner — `animate-spin` on the canonical Loader2 path. */
function Loader2({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
    >
      <path d={SPINNER_PATH} />
    </svg>
  );
}

/** Status pill colour + label. Live = green, reconnecting = amber, off = red. */
function statusVisual(status: SSEStatus): {
  bg: string;
  fg: string;
  border: string;
  icon: "dot" | "spinner" | "off";
  labelKey: "live" | "reconnecting" | "disconnected" | "paused" | "connecting";
} {
  switch (status) {
    case "open":
      return {
        bg: "hsl(152 56% 18%)",
        fg: "hsl(150 70% 80%)",
        border: "hsl(152 56% 32%)",
        icon: "dot",
        labelKey: "live",
      };
    case "reconnecting":
      return {
        bg: "hsl(36 70% 18%)",
        fg: "hsl(36 90% 80%)",
        border: "hsl(36 70% 36%)",
        icon: "spinner",
        labelKey: "reconnecting",
      };
    case "connecting":
      return {
        bg: "hsl(220 14% 14%)",
        fg: "hsl(215 20% 80%)",
        border: "hsl(220 14% 32%)",
        icon: "spinner",
        labelKey: "connecting",
      };
    case "paused":
      return {
        bg: "hsl(220 14% 14%)",
        fg: "hsl(215 20% 80%)",
        border: "hsl(220 14% 32%)",
        icon: "off",
        labelKey: "paused",
      };
    default:
      return {
        bg: "hsl(354 78% 18%)",
        fg: "hsl(354 88% 80%)",
        border: "hsl(354 78% 36%)",
        icon: "off",
        labelKey: "disconnected",
      };
  }
}

/** Format a Date-or-ISO as a localised relative time string ("3 min ago"). */
function formatRelative(value: Date | string | null, locale: string): string | null {
  if (!value) return null;
  const ms =
    value instanceof Date ? value.getTime() : new Date(value).getTime();
  if (!Number.isFinite(ms)) return null;
  const diffSec = Math.round((ms - Date.now()) / 1000);
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    const abs = Math.abs(diffSec);
    if (abs < 60) return rtf.format(diffSec, "second");
    if (abs < 3600) return rtf.format(Math.round(diffSec / 60), "minute");
    if (abs < 86_400) return rtf.format(Math.round(diffSec / 3600), "hour");
    return rtf.format(Math.round(diffSec / 86_400), "day");
  } catch {
    return new Date(ms).toISOString();
  }
}

function formatEta(eta: string | null, locale: string): string | null {
  if (!eta) return null;
  try {
    return new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(eta));
  } catch {
    return eta;
  }
}

export function VesselOverlayPanel({
  name,
  mmsi,
  latest,
  nextPort,
  eta,
  status,
  onRetry,
  t,
  locale,
  className,
}: VesselOverlayPanelProps) {
  const visual = statusVisual(status);
  const isReconnecting = status === "reconnecting" || status === "connecting";
  const isLive = status === "open";

  const speed =
    latest?.speedKnots != null
      ? `${latest.speedKnots.toFixed(1)} ${t("units.knots")}`
      : t("unknown");
  const headingDeg = latest?.headingDegrees;
  const heading =
    typeof headingDeg === "number"
      ? `${headingDeg}${t("units.degrees")}`
      : t("unknown");
  const etaFmt = formatEta(eta, locale) ?? t("unknown");
  const lastFix = formatRelative(latest?.recordedAt ?? null, locale) ?? t("unknown");

  return (
    <aside
      aria-label={t("title")}
      className={[
        "rounded-lg border bg-card/95 text-card-foreground shadow-sm backdrop-blur-sm", /* uncodixify-ok: map overlay panel — subtle blur anchors the panel above the tile layer */
        "px-4 py-3 w-[260px] text-sm",
        className ?? "",
      ].join(" ")}
      style={{
        // Fallback values when CSS custom properties are absent (jsdom tests).
        background: "hsl(220 14% 10% / 0.92)",
        color: "hsl(0 0% 95%)",
        border: "1px solid hsl(220 14% 26%)",
      }}
    >
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p
            className="truncate font-semibold"
            style={{ fontSize: "0.95rem" }}
          >
            {name || t("loading")}
          </p>
          <p
            className="truncate"
            style={{ fontSize: "0.72rem", opacity: 0.7 }}
          >
            {t("subtitle", { name: name || "—", mmsi })}
          </p>
        </div>
        <span
          role="status"
          aria-live="polite"
          data-testid="vessel-status-pill"
          className="inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium"
          style={{
            background: visual.bg,
            color: visual.fg,
            borderColor: visual.border,
            whiteSpace: "nowrap",
          }}
        >
          {visual.icon === "dot" && (
            <span
              aria-hidden="true"
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: visual.fg,
                display: "inline-block",
              }}
            />
          )}
          {visual.icon === "spinner" && (
            <Loader2 className="animate-spin" />
          )}
          {visual.icon === "off" && (
            <svg
              aria-hidden="true"
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <line x1="4" y1="4" x2="20" y2="20" />
            </svg>
          )}
          {t(`status.${visual.labelKey}`)}
        </span>
      </header>

      <dl
        className="mt-3 grid grid-cols-2 gap-y-1.5 gap-x-3"
        data-testid="vessel-overlay-fields"
      >
        <dt style={{ opacity: 0.65 }}>{t("fields.speed")}</dt>
        <dd
          className="text-end font-medium tabular-nums"
          aria-label={`${t("fields.speed")}: ${speed}`}
        >
          {speed}
        </dd>
        <dt style={{ opacity: 0.65 }}>{t("fields.heading")}</dt>
        <dd
          className="text-end font-medium tabular-nums"
          aria-label={`${t("fields.heading")}: ${heading}`}
        >
          {heading}
        </dd>
        <dt style={{ opacity: 0.65 }}>{t("fields.eta")}</dt>
        <dd className="text-end font-medium">
          {etaFmt}
        </dd>
        <dt style={{ opacity: 0.65 }}>{t("fields.nextPort")}</dt>
        <dd className="text-end font-medium truncate">
          {nextPort ?? t("unknown")}
        </dd>
        <dt style={{ opacity: 0.65 }}>{t("fields.lastFix")}</dt>
        <dd className="text-end font-medium">{lastFix}</dd>
      </dl>

      {/* Retry row — touch target via parent min-height, not Switch-style. */}
      {!isLive && (
        <div
          className="mt-3 flex items-center justify-between p-2 min-h-[44px] rounded-md"
          style={{ background: "hsl(0 0% 100% / 0.04)" }}
        >
          <span style={{ fontSize: "0.78rem", opacity: 0.8 }}>
            {isReconnecting
              ? t("status.reconnecting")
              : t("status.disconnected")}
          </span>
          <button
            type="button"
            onClick={onRetry}
            className="rounded-md px-3 py-1 text-xs font-medium underline-offset-2 hover:underline transition-colors duration-150"
            style={{
              background: "transparent",
              color: visual.fg,
              border: `1px solid ${visual.border}`,
              cursor: "pointer",
            }}
          >
            {t("retry")}
          </button>
        </div>
      )}
    </aside>
  );
}
